import { useEffect, useState } from "react"
import type { ReactNode } from "react"

import { Navigate, type RouteObject } from "react-router-dom"

import type { TrainingModule, TrainingRoute, TrainingTrack } from "./routes.types"
import type { Route } from "@/api/models"

import { DashPage } from "@/app/pages/modules/dash-page"
import { DocumentEditorPage } from "@/app/pages/modules/document-editor-page"
import { DocumentNewPage } from "@/app/pages/modules/document-new-page"
import { DocumentsLandingPage } from "@/app/pages/modules/documents-landing-page"
import { SegmentAnalysisPage } from "@/app/pages/modules/segment-analysis-page"
import { UseCasesDashboardPage } from "@/app/pages/modules/use-cases-dashboard-page"
import { UseCasesPage } from "@/app/pages/modules/use-cases-page"
import { TrainingHomePage } from "@/app/pages/training/training-page"
import { ModuleShell } from "@/components/layouts/module-shell"
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar"
import { TenderCasePage } from "@/features/tender/components/tender-case-page"
import { TrainingLayout } from "@/features/training/components/layouts/training-layout"
import { WorkflowEditorPage } from "@/features/workflow-editor/pages/workflow-editor-page"
import { WorkflowLandingPage } from "@/features/workflow-editor/pages/workflow-landing-page"
import { isFeatureEnabledInConfig } from "@/hooks/feature-utils"
import { logger } from "@/lib/logger"

import { LegacyPathRedirect } from "./legacy-path-redirect"
import {
  applyAgentsModuleDescription,
  buildAgentsDetailRouteObjects,
  buildWorkflowRouteObjects,
  gateHostedAgentsRoutes,
  isStandaloneRouteOverride,
  prependRouteObjects,
} from "./route-object-utils"
import { routePageComponents, trainingSectionComponents } from "./routes-route-registry"
import { RoutesContext } from "./use-routes"
import { SettingsService } from "../services/settings-service"

// Modules renamed in place keep their old URLs working (bookmarks, sent emails,
// open tabs) via a LegacyPathRedirect mounted at `<legacyPath>/*`.
const LEGACY_PATH_ALIASES = [{ legacyPath: "/anbud", routeId: "tender" }]

export function RoutesProvider({ children }: { children: ReactNode }) {
  const [routes, setRoutes] = useState<Route[]>([])
  const [routeObjects, setRouteObjects] = useState<RouteObject[]>([])
  const [trainingModules, setTrainingModules] = useState<TrainingModule[]>([])
  const [trainingTracks, setTrainingTracks] = useState<TrainingTrack[]>([])
  const [trainingRoutes, setTrainingRoutes] = useState<TrainingRoute | null>(null)

  const trainingEnabled = !!trainingRoutes

  useEffect(() => {
    const handleInitialized = () => {
      try {
        const settings = SettingsService.getInstance().settings
        // Hosted agents are on-prem only: with the deployment switch off the
        // Agents module is dropped here, before any route, sub-route or nav
        // entry is derived from the backend list.
        const backend = gateHostedAgentsRoutes(settings, <Navigate replace to="/" />)
        // Collect IDs of routes already provided by backend (these have correct enabled status from Cosmos DB)
        const backendRouteIds = new Set(backend.routes.map((r: Route) => r.id))

        // Standalone modules are pushed separately with full element/component config below.
        // Filter them from the backend map to avoid duplicates.
        const routesConfig = backend.routes
          .filter((route: Route) => !isStandaloneRouteOverride(route))
          .map(applyAgentsModuleDescription)
          .map((route: Route) => {
            if (typeof routePageComponents[route.component] !== "function") {
              logger.warn(`No component found for ${route.component}`)
              return null
            }
            return {
              ...route,
              element: (
                <div key={route.path} className="flex h-full min-h-0 flex-col">
                  {routePageComponents[route.component]({
                    header: route.header,
                    navigation_text: route.navigation_text,
                    info_text: route.info_text,
                    example_questions: route.example_questions,
                    session_type: route.id,
                    overrides: route.overrides,
                  })}
                </div>
              ),
            }
          })

        routesConfig.push({
          id: "projects",
          name: "Projects",
          path: "/projects/:projectId?",
          component: "projects",
          enabled: backendRouteIds.has("projects"),
          header: "Projects",
          navigation_text: "Projects",
          info_text: "",
          example_questions: [],
          element: (
            <div key="/projects/:projectId?">
              {routePageComponents["projects"]({
                header: "Projects",
                navigation_text: "Projects",
                info_text: "",
                example_questions: [],
                session_type: "projects",
                overrides: undefined,
              })}
            </div>
          ),
        })

        // Add AI Portfolio route (standalone module, no chat sidebar)
        routesConfig.push({
          id: "ai-portfolio",
          name: "AI Portfolio",
          path: "/ai-portfolio",
          component: "ai-portfolio",
          enabled: backendRouteIds.has("ai-portfolio"),
          header: "AI Portfolio",
          navigation_text: "AI Portfolio",
          info_text: "useCases.moduleDescription",
          example_questions: [],
          overrides: {
            disableWebSearch: true,
            disableWorkflows: true,
            disablePrompts: true,
            disableAgent: true,
          },
          element: (
            <ModuleShell>
              <UseCasesPage />
            </ModuleShell>
          ),
        })

        // Add Dash BI route (standalone module, disabled by default)
        routesConfig.push({
          id: "dash",
          name: "Dash",
          path: "/dash",
          component: "dash",
          enabled: backendRouteIds.has("dash"),
          header: "Dash",
          navigation_text: "Dash",
          info_text: "dash.moduleDescription",
          example_questions: [],
          overrides: {
            disableWorkflows: true,
            disablePrompts: true,
            disableAgent: true,
          },
          element: (
            <SidebarProvider className="relative min-h-full">
              <DashPage />
            </SidebarProvider>
          ),
        })

        // Add Segment Analysis route (standalone demo module, disabled by default)
        routesConfig.push({
          id: "segment-analysis",
          name: "Segment Analysis",
          path: "/segment-analysis",
          component: "segment-analysis",
          enabled: backendRouteIds.has("segment-analysis"),
          header: "Segment Analysis",
          navigation_text: "Segment Analysis",
          info_text: {
            sv: "AI-assisterade marknadssegmentplaner.",
            en: "AI-assisted market segment plans.",
          },
          example_questions: [],
          overrides: {
            disableWebSearch: true,
            disableWorkflows: true,
            disablePrompts: true,
            disableAgent: true,
          },
          element: (
            <ModuleShell>
              <SegmentAnalysisPage />
            </ModuleShell>
          ),
        })

        // Flows is not a separately-gated frontend route in Cosmos — tie module visibility
        // to Explorer being enabled plus the admin `explorer_flows` feature toggle (same
        // gate as useWorkflowsEnabled / the Explorer sidebar workflows folder).
        const explorerEnabled = backend.routes.some((r) => r.component === "free" && r.enabled)
        const flowsFeatureEnabled = isFeatureEnabledInConfig(settings.features, "explorer_flows")

        routesConfig.push({
          id: "workflows",
          name: "Flows",
          path: "/flows",
          component: "workflows",
          enabled: explorerEnabled && flowsFeatureEnabled,
          header: "Flows",
          navigation_text: "Flows",
          info_text: "workflowLanding.subtitle",
          example_questions: [],
          element: <WorkflowLandingPage />,
        })

        const primaryExplorerRoute = routesConfig.find(
          (route) => route?.component === "free" && route.enabled
        )
        const primaryExplorerSessionType = primaryExplorerRoute?.id ?? "open"

        // Built-in Documents route metadata. This is intentionally not part of customer frontend route config.
        // Keep it disabled so it is not treated as a module route (chat path generation, module selector),
        // while still allowing path -> session resolution in shared hooks/components.
        routesConfig.push({
          id: "documents",
          name: "Documents",
          path: "/documents",
          component: "projects",
          enabled: false,
          header: "Documents",
          navigation_text: "Documents",
          info_text: "",
          example_questions: [],
          overrides: {
            overrideSessionType: primaryExplorerSessionType,
          },
          element: (
            <SidebarProvider className="relative min-h-full w-full">
              <SidebarInset className="w-full">
                <DocumentsLandingPage />
              </SidebarInset>
            </SidebarProvider>
          ),
        })

        // Diagrams are nested under explorer so they inherit the "open" session type naturally
        // This ensures chat history, projects, etc. all load correctly on diagram pages

        setRoutes(
          routesConfig.filter(
            (route): route is NonNullable<typeof route> => route !== null && route.enabled
          )
        )

        // Root-mounted Explorer (path "/") has no nested projects route (the
        // nested-route loop below skips "/"), so treat it as having no target.
        const explorerWithProjects =
          primaryExplorerRoute && primaryExplorerRoute.path !== "/" ? primaryExplorerRoute : null
        const explorerProjectsPath = `${explorerWithProjects?.path ?? "/explorer"}/projects`
        const projectsModuleEnabled = backendRouteIds.has("projects")
        const projectsRoute = routesConfig.find(
          (route): route is NonNullable<typeof route> => route !== null && route.id === "projects"
        )

        // Only register sub-routes whose parent module is enabled, so disabled
        // modules can't be reached via direct URL (matching the main-route gate).
        const isModuleEnabled = (id: string) => routesConfig.some((r) => r?.id === id && r.enabled)

        const workflowRoutes = isModuleEnabled("workflows")
          ? buildWorkflowRouteObjects({
              editor: <WorkflowEditorPage />,
              landing: <WorkflowLandingPage />,
            })
          : []

        const moduleRoutes: RouteObject[] = routesConfig
          .filter(
            (route): route is NonNullable<typeof route> =>
              route !== null && route.enabled && route.id !== "workflows" && route.id !== "projects"
          )
          .map((route) => {
            return {
              ...(route.path === "/" ? { index: true } : { path: route.path }),
              element: route.element,
            }
          })

        const childrenRoutes: RouteObject[] = prependRouteObjects(workflowRoutes, moduleRoutes)

        // Always register /projects — redirect to nested explorer projects when module
        // disabled. If no Explorer route is enabled either (locked-down deployments),
        // the nested target is never mounted — send the user home instead of a 404.
        childrenRoutes.push({
          path: "/projects/:projectId?",
          element:
            projectsModuleEnabled && projectsRoute ? (
              projectsRoute.element
            ) : (
              <Navigate replace to={explorerWithProjects ? explorerProjectsPath : "/"} />
            ),
        })

        // Add nested project routes for each module (e.g., /custom-variant/projects/:projectId)
        // These are routing-only, not added to the module dropdown
        backend.routes.forEach((route: Route) => {
          if (!route.enabled || route.path === "/") return

          try {
            const routePath = route.path.replace(/^\//, "") // Remove leading slash
            const nestedProjectPath = `/${routePath}/projects/:projectId?`
            childrenRoutes.push({
              path: nestedProjectPath,
              element: (
                <div key={nestedProjectPath}>
                  {routePageComponents["projects"]({
                    header: route.header,
                    navigation_text: route.navigation_text,
                    info_text: route.info_text,
                    example_questions: route.example_questions,
                    session_type: route.id,
                    overrides: route.overrides,
                  })}
                </div>
              ),
            })
          } catch (e) {
            logger.warn(`Failed to add nested project route for ${route.id}`, e as Error)
          }
        })

        // Add dynamic chat routes for ALL configured routes
        routesConfig.forEach((route) => {
          if (!route || !route.enabled) return

          // Determine chat path from route config or construct it
          let chatPath: string
          if (route.overrides?.chatPath) {
            // Use explicit chatPath from overrides (remove leading slash for relative path)
            chatPath = (route.overrides.chatPath as string).replace(/^\//, "")
          } else {
            // All routes now have named paths (no root / route)
            chatPath = `${route.path.replace(/^\//, "")}/chat/:sessionId`
          }

          // Skip if already added as a base route, projects route, or workflows
          if (route.id === "projects" || route.id === "workflows") return

          // Add chat route using the same component as the base route
          childrenRoutes.push({
            path: chatPath,
            element: route.element,
          })
        })

        // Hosted-agent detail view (HERMES): /agents/:agentSlug (see route-object-utils)
        childrenRoutes.push(
          ...buildAgentsDetailRouteObjects(routesConfig.find((route) => route?.id === "agents")),
          // Hosted agents switched off: old agents links go home, not to a 404.
          ...backend.hostedAgentsRedirects
        )

        if (isModuleEnabled("ai-portfolio")) {
          childrenRoutes.push({
            path: "/ai-portfolio/dashboard",
            element: (
              <ModuleShell>
                <UseCasesDashboardPage />
              </ModuleShell>
            ),
          })
        }

        childrenRoutes.push({
          path: "/documents",
          element: (
            <SidebarProvider className="relative min-h-full w-full">
              <SidebarInset className="w-full">
                <DocumentsLandingPage />
              </SidebarInset>
            </SidebarProvider>
          ),
        })

        childrenRoutes.push({
          path: "/documents/new",
          element: (
            <SidebarProvider className="relative min-h-full w-full">
              <SidebarInset className="w-full">
                <DocumentNewPage />
              </SidebarInset>
            </SidebarProvider>
          ),
        })

        childrenRoutes.push({
          path: "/documents/:id",
          element: (
            <SidebarProvider className="relative min-h-full w-full">
              <SidebarInset className="w-full">
                <DocumentEditorPage />
              </SidebarInset>
            </SidebarProvider>
          ),
        })

        if (isModuleEnabled("admin-dashboard")) {
          const adminPath =
            routesConfig.find((route) => route?.id === "admin-dashboard")?.path ?? "/admin"

          childrenRoutes.push({
            path: "/admin-dashboard",
            element: <Navigate replace to={adminPath} />,
          })

          childrenRoutes.push({
            path: "/administration",
            element: <Navigate replace to={adminPath} />,
          })
        }

        // Add detail sub-routes for any route that declares overrides.detailPath (e.g. the tender module)
        routesConfig.forEach((route) => {
          if (!route?.enabled || !route.overrides?.detailPath) return
          const detailPath = route.overrides.detailPath as string
          childrenRoutes.push({
            path: detailPath,
            element: (
              <SidebarProvider className="relative min-h-full w-full">
                <TenderCasePage />
              </SidebarProvider>
            ),
          })
        })

        // Redirect legacy module paths to their renamed equivalents. Only mounted
        // when the module is enabled and no longer occupies the legacy path itself —
        // tenants whose route config still uses the legacy path mount the module
        // there as before. A trailing-splat route also matches the bare legacy path.
        LEGACY_PATH_ALIASES.forEach(({ legacyPath, routeId }) => {
          const target = routesConfig.find(
            (route): route is NonNullable<typeof route> =>
              route !== null && route.id === routeId && route.enabled
          )
          if (!target || target.path === legacyPath) return
          childrenRoutes.push({
            path: `${legacyPath}/*`,
            element: <LegacyPathRedirect to={target.path} />,
          })
        })

        childrenRoutes.push({
          path: "*",
          lazy: () => import("../app/pages/not-found-page"),
        })

        // Only return enabled training modules and sections
        const enabledTrainingModules = (settings.frontend.training_modules as TrainingModule[])
          .filter((module) => module.enabled)
          .map((module) => {
            return {
              ...module,
              sections: module.sections.filter((section) => section.enabled),
            }
          })

        const enabledTrainingRoutes = enabledTrainingModules.flatMap((module) =>
          module.sections
            .map((section) => {
              const component = trainingSectionComponents[section.component]
              if (!component) return null
              return {
                url: section.url.replace(/^\/training\/?/, ""),
                component,
              }
            })
            .filter((route): route is NonNullable<typeof route> => route !== null)
        )

        const enabledTrainingRoutesWithLayout = {
          path: "training",
          element: <TrainingLayout />,
          children: [
            { index: true, element: <TrainingHomePage /> },
            ...enabledTrainingRoutes.map((route) => ({
              path: route.url,
              element: route.component,
            })),
          ],
        }

        // Parse training tracks
        const rawTracks = ((settings.frontend as Record<string, unknown>).training_tracks ??
          []) as TrainingTrack[]

        setRouteObjects(childrenRoutes)
        setTrainingModules(enabledTrainingModules)
        setTrainingTracks(rawTracks)
        setTrainingRoutes(
          enabledTrainingRoutesWithLayout.children.length > 1
            ? enabledTrainingRoutesWithLayout
            : null
        )
      } catch (error) {
        logger.error("Failed to fetch routes:", error as Error)
      }
    }

    SettingsService.initializedEvent.on("initialized", handleInitialized)

    // Cleanup event listener on unmount
    return () => {
      SettingsService.initializedEvent.off("initialized", handleInitialized)
    }
  }, [])

  // Dynamic route utilities
  const getRouteById = (routeId: string): Route | undefined => {
    return routes.find((route) => route.id === routeId)
  }

  const getRoutePath = (routeId: string): string => {
    const route = getRouteById(routeId)
    return route?.path ?? "/explorer"
  }

  const getRouteChatPath = (routeId: string): string => {
    const route = getRouteById(routeId)
    // If route has explicit chatPath in overrides, use it
    if (route?.overrides?.chatPath) {
      return route.overrides.chatPath as string
    }
    // Otherwise construct from path
    if (route?.path === "/") {
      return "/chat"
    }
    return route?.path ? `${route.path}/chat` : "/chat"
  }

  return (
    <RoutesContext.Provider
      value={{
        routes,
        routeObjects,
        trainingModules,
        trainingTracks,
        trainingRoutes,
        trainingEnabled,
        getRouteById,
        getRoutePath,
        getRouteChatPath,
      }}
    >
      {children}
    </RoutesContext.Provider>
  )
}
